import { type PropsWithChildren, Suspense } from 'react';

import { DotsThreeVertical } from '@phosphor-icons/react/dist/ssr';
import classNames from 'classnames';

import { AvatarUploadWrapper } from '@/ui/AvatarUploadWrapper';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/ui/DropdownMenu';

import { useComponentContext } from '../context/useComponentContext';

import { PostCommentFormElement } from '../../postCommentFormElement';
import type { CommentData } from '../../types';
import { commentAuthorBorder } from '../PostCommentsFeed.css';
import { CommentDisplay } from './CommentDisplay';
import CommentLikeAction from './CommentLikeAction';
import CommentReplyAction from './CommentReplyAction';
import { InlineEditForm } from './InlineEditForm';

type Props = PropsWithChildren<{
  comment: CommentData;
}>;

export function Comment({ comment, children }: Props) {
  const {
    canReply,
    showActions,
    isLive,
    replyNode,
    isSubmitting,
    onCommentChange,
    onClickReply,
    replyingToCommentId,
    onClickAction,
    avatarUrl,
    onClickLogin,
    onClickSubscribe,
    translations,
    isLoggedInAsAdmin,
    isVisitorLoggedIn,
    commenterId,
    editingCommentId,
    onEditComment,
    onCancelEdit,
    isEditSubmitting,
    onAvatarUpload,
    isAvatarUploading,
    canUploadAvatar,
  } = useComponentContext();

  const handleClickReply = () => {
    onClickReply?.(replyingToCommentId ? null : comment.id);
  };

  const handleClickCancel = () => {
    onClickReply?.(null);
  };

  const isHighlighted = false; // TODO
  const isMaxDepthLevel = comment.depth_level >= comment.max_allowed_depth_level;
  const isEditing = editingCommentId === comment.id;

  const handleClickDelete = () => {
    onClickAction?.('delete', comment);
  };

  const handleClickReport = () => {
    onClickAction?.('report', comment);
  };

  const handleClickEdit = () => {
    onClickAction?.('edit', comment);
  };

  const isModeratorComment = comment.is_moderator_comment;
  const showDeleteOption = comment.can_delete || isLoggedInAsAdmin;
  const showEditOption = comment.can_edit;
  const subCommentsCount = comment.sub_comments.length > 0 ? comment.sub_comments.length : 0;

  // Check if this comment belongs to the logged-in user (for avatar upload)
  const isOwnComment = !!commenterId && String(comment.commenter_id) === String(commenterId);

  // Show dropdown if actions are enabled and (not a moderator comment OR user can edit their moderator comment)
  const showDropdown = showActions && (!isModeratorComment || showEditOption);

  return (
    <div className="flex flex-col gap-y-2">
      <div
        key={comment.id}
        className={classNames('flex w-full justify-between transition-all', isHighlighted && 'animate-pulse')}
        id={comment.id}
      >
        <div className="flex flex-row gap-x-3 w-full">
          <div className="flex flex-col h-full justify-start items-center">
            <div className="grow-1 shrink-0">
              <AvatarUploadWrapper
                avatarUrl={isOwnComment ? avatarUrl : comment.avatar_url}
                onFileSelect={onAvatarUpload}
                isUploading={isAvatarUploading}
                canUpload={isOwnComment && canUploadAvatar}
                size={32}
              />
            </div>
            <div className={classNames('w-px h-full grow-1', commentAuthorBorder)}>&nbsp;</div>
          </div>
          <div className="w-full flex flex-col gap-y-3">
            <div className="w-full flex flex-col gap-y-2">
              {isEditing && onEditComment && onCancelEdit ? (
                <InlineEditForm
                  comment={comment}
                  onSave={onEditComment}
                  onCancel={onCancelEdit}
                  isSubmitting={isEditSubmitting}
                  translations={translations}
                  textareaBorderColor={replyNode?.attrs?.textareaBorderColor}
                  textareaBorderRadius={replyNode?.attrs?.textareaBorderRadius?.toString()}
                  textareaPadding={replyNode?.attrs?.textareaPadding?.toString()}
                  textareaTextColor={replyNode?.attrs?.textareaTextColor}
                  saveButtonBackgroundColor={replyNode?.attrs?.submitButtonBackgroundColor}
                  saveButtonTextColor={replyNode?.attrs?.submitButtonTextColor}
                  cancelButtonBackgroundColor={replyNode?.attrs?.cancelButtonBackgroundColor}
                  cancelButtonTextColor={replyNode?.attrs?.cancelButtonTextColor}
                />
              ) : (
                <CommentDisplay
                  name={comment.commenter}
                  content={comment.content}
                  time={comment.created_at}
                  updatedTime={comment.updated_at}
                  isModerator={isModeratorComment}
                  editedLabel={translations?.['comments.editing.edited']}
                  authorLabel={translations?.['author.author']}
                />
              )}

              {!isEditing && (
                <div className="flex items-center gap-x-4">
                  <CommentLikeAction comment={comment} />
                  {!isMaxDepthLevel && canReply && (
                    <CommentReplyAction onClick={handleClickReply} subCommentsCount={subCommentsCount} />
                  )}
                </div>
              )}

              {canReply && replyingToCommentId === comment.id && replyNode && (
                <PostCommentFormElement
                  isSubmitting={isSubmitting}
                  isLive={isLive}
                  element={replyNode}
                  onCommentChange={onCommentChange}
                  onCancel={handleClickCancel}
                  isVisitorLoggedIn={isVisitorLoggedIn}
                  avatarUrl={avatarUrl}
                  onClickLogin={onClickLogin}
                  onClickSubscribe={onClickSubscribe}
                  translations={translations}
                  commenterId={commenterId}
                  onAvatarUpload={onAvatarUpload}
                  isAvatarUploading={isAvatarUploading}
                  canUploadAvatar={canUploadAvatar}
                >
                  <input type="hidden" value={comment.id} name="comment_id" />
                </PostCommentFormElement>
              )}
            </div>
          </div>
        </div>

        {showDropdown && !isEditing && (
          <Suspense>
            <DropdownMenu>
              <DropdownMenuTrigger className="hover:bg-gray-100">
                <DotsThreeVertical />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {showEditOption && (
                  <DropdownMenuItem onClick={handleClickEdit}>
                    {translations?.['comments.editing.edit'] || 'Edit'}
                  </DropdownMenuItem>
                )}
                {showDeleteOption && !isModeratorComment && (
                  <DropdownMenuItem onClick={handleClickDelete} isDestructive>
                    Delete
                  </DropdownMenuItem>
                )}
                {!showDeleteOption && !isModeratorComment && (
                  <DropdownMenuItem onClick={handleClickReport} isDestructive>
                    Report
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </Suspense>
        )}
      </div>
      {children && (
        <div className="pl-8 w-full">
          {/* Sub-comments */}
          {children}
        </div>
      )}
    </div>
  );
}
